import { ref } from 'vue'

import { closeModal, openModal } from '@ds/components/ModalBase'

import type { ChatRequestPageContext } from '~/api/shopping-assistant/types'

import { useShoppingAssistantWebSidebar } from '../stores/useShoppingAssistantWebSidebar'

import type { EntryPoint } from './useShoppingAssistantContext'

export interface AssistantEntryPayload {
  message: string
  pageContext: ChatRequestPageContext | null
  // Opaque payload echoed back to onLaunch (e.g. the clicked pill id).
  meta: Record<string, unknown>
}

/**
 * Launches the web sidebar assistant from an in-page entry point (the search
 * bar's "search with AI", the PDP suggestion pills), orchestrating the
 * open-or-confirm-then-restart flow around `useShoppingAssistantWebSidebar`:
 *
 * - sidebar closed → open() a brand-new conversation seeded with the message
 *   and page context;
 * - sidebar open with an empty conversation → restart() it (nothing to lose);
 * - sidebar open with a real conversation → ask for confirmation first, then
 *   restart() on accept and leave the conversation untouched on decline.
 *
 * The confirmation dialog itself is rendered by the consumer through
 * ShoppingAssistantNewConversationDialog (one RevDialog name per entry
 * point), wired to the returned accept()/decline().
 *
 * `onLaunch` fires whenever a conversation is actually opened or restarted —
 * immediately, or after the dialog is accepted — and never on decline, so it
 * is the right seam for entry tracking.
 */
export function useShoppingAssistantEntry({
  dialogName,
  entryPoint,
  onLaunch,
}: {
  dialogName: string
  entryPoint: EntryPoint
  onLaunch?: (payload: AssistantEntryPayload) => void
}) {
  const store = useShoppingAssistantWebSidebar()

  const pendingPayload = ref<AssistantEntryPayload | null>(null)

  function launch({
    message = '',
    pageContext = null,
    meta = {},
  }: {
    message?: string
    pageContext?: ChatRequestPageContext | null
    meta?: Record<string, unknown>
  } = {}): void {
    const payload: AssistantEntryPayload = { message, pageContext, meta }

    if (!store.isOpen) {
      store.open({
        entryPoint,
        message,
        pageContext,
      })
      onLaunch?.(payload)

      return
    }

    // Confirm only when there's a real conversation to lose — same signal
    // the web-sidebar host adapter uses (see useWebSidebarShoppingAssistantHost's
    // `newChat`), so an open-but-empty panel is replaced directly instead of
    // prompting for nothing.
    if (store.hasUserMessages) {
      pendingPayload.value = payload
      openModal(dialogName)

      return
    }

    store.restart({
      entryPoint,
      message,
      pageContext,
    })
    onLaunch?.(payload)
  }

  function accept(): void {
    closeModal(dialogName)

    const payload = pendingPayload.value
    pendingPayload.value = null

    if (!payload) return

    store.restart({
      entryPoint,
      message: payload.message,
      pageContext: payload.pageContext,
    })
    onLaunch?.(payload)
  }

  function decline(): void {
    closeModal(dialogName)
    pendingPayload.value = null
  }

  return {
    launch,
    accept,
    decline,
  }
}
